import { useState } from 'react';

/**
 * Hook to extract filteration logic for table entries
 * Filters table entries displayed by entries selected for each dropdown type
 *
 * Also performs additional filteration by search terminology, if used
 * Returns filtered data to be rendered in productTable
 */

interface FilterParams {
  [key: string]: string | undefined;
  searchValue: string;
}

interface FilterConfig {
  filterFields: Record<string, string>;
  searchableFields: string[];
  // fields which should match by specific id
  idMatchFields?: string[];
}

export default function useTableFilter<T extends { id: string; [key: string]: string | undefined }>(
  config: FilterConfig,
  initialData: T[] = [],
) {
  const [data, setData] = useState<T[]>(initialData);
  const [filteredData, setFilteredData] = useState<T[]>(initialData);
  const [inputValue, setInputValue] = useState('');

  const applyFilters = (dataToFilter: T[], filters: FilterParams) => {
    let filtered = [...dataToFilter];

    // apply each configured filter on data
    Object.entries(config.filterFields).forEach(([key, field]) => {
      const filterValue = filters[key];

      if (filterValue) {
        // Check if this field should match by ID
        if (config.idMatchFields?.includes(key)) {
          // Match either by ID or by field value
          filtered = filtered.filter((item) => item.id.includes(filterValue) || String(item[field]) === filterValue);
        } else {
          filtered = filtered.filter((item) => String(item[field]) === filterValue);
        }
      }
    });

    // Filter by search input if provided
    if (filters.searchValue) {
      const searchRegex = new RegExp(filters.searchValue, 'i');
      filtered = filtered.filter((item) =>
        config.searchableFields.some((field) => {
          const value = item[field];
          return value && searchRegex.test(String(value));
        }),
      );
    }

    return filtered;
  };

  return {
    data,
    setData,
    filteredData,
    setFilteredData,
    inputValue,
    setInputValue,
    applyFilters,
  };
}
